import { useState, useEffect } from 'react';
import { AudioMixer, type MixRatio } from '../utils/audioMixer';
import './AudioMixerControls.css';

interface AudioMixerControlsProps {
    mixer: AudioMixer;
    onMixRatioChange?: (ratio: MixRatio) => void;
}

export default function AudioMixerControls({ mixer, onMixRatioChange }: AudioMixerControlsProps) {
    const [microphoneGain, setMicrophoneGain] = useState(0.5);
    const [systemGain, setSystemGain] = useState(0.5);

    // Initialize with current mixer values
    useEffect(() => {
        const currentRatio = mixer.getMixRatio();
        if (currentRatio) {
            setMicrophoneGain(currentRatio.microphone);
            setSystemGain(currentRatio.system);
        }
    }, [mixer]);

    const handleMicrophoneChange = (value: number) => {
        setMicrophoneGain(value);
        const newRatio: MixRatio = {
            microphone: value,
            system: systemGain,
        };
        mixer.setMixRatio(newRatio);
        onMixRatioChange?.(newRatio);
    };

    const handleSystemChange = (value: number) => {
        setSystemGain(value);
        const newRatio: MixRatio = {
            microphone: microphoneGain,
            system: value,
        };
        mixer.setMixRatio(newRatio);
        onMixRatioChange?.(newRatio);
    };

    const formatPercentage = (value: number) => {
        return `${Math.round(value * 100)}%`;
    };

    return (
        <div className="audio-mixer-controls">
            <h3>Audio Mix Controls</h3>
            
            <div className="mixer-control">
                <label htmlFor="microphone-gain">
                    Microphone Level: {formatPercentage(microphoneGain)}
                </label>
                <input
                    id="microphone-gain"
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={microphoneGain}
                    onChange={(e) => handleMicrophoneChange(parseFloat(e.target.value))}
                    className="gain-slider"
                />
            </div>

            <div className="mixer-control">
                <label htmlFor="system-gain">
                    System Audio Level: {formatPercentage(systemGain)}
                </label>
                <input
                    id="system-gain"
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={systemGain}
                    onChange={(e) => handleSystemChange(parseFloat(e.target.value))}
                    className="gain-slider"
                />
            </div>

            <div className="mixer-info">
                <p>Adjust the sliders to balance microphone and system audio levels.</p>
            </div>
        </div>
    );
}
